/**
 * SMS-ის ნაკადები — „ინტერაქციის" მეორე არხი.
 *
 * იგივე თანმიმდევრობა, რაც მეილს: ჯერ კრედენციალები, მერე საცდელი
 * შეტყობინება, მერე „ვის მიუვიდოდა" და მხოლოდ ბოლოს ჩამრთველი.
 */

import { useEffect, useState } from 'react';
import { KeyRound, MessageSquare, Play, Save, Send } from 'lucide-react';
import { Alert, Button, Card, Field, Spinner } from '../../components/ui';
import { useToast } from '../../components/Toast';
import { ApiError } from '../../api/client';
import {
  useRunAbsenceSms,
  useSaveSmsCredentials,
  useSendTestSms,
  useSmsCredentials,
  useSmsFlows,
  useSmsLogs,
  useUpdateSmsFlow,
  type AbsenceSmsRun,
  type SmsFlow,
} from '../../api/queries';

const EVENT_LABEL: Record<string, string> = {
  password_reset: 'პაროლის აღდგენა',
  student_absence: 'გაცდენის შეტყობინება',
};

const STATUS_STYLE: Record<string, string> = {
  SENT: 'text-emerald-700 bg-emerald-50',
  FAILED: 'text-rose-700 bg-rose-50',
  SKIPPED: 'text-ink-mute bg-surface-2',
};

export function SmsFlowsPanel() {
  const { data: flows, isLoading } = useSmsFlows();

  return (
    <div className="space-y-6">
      <SmsCredentialsCard />

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner className="size-6" />
        </div>
      ) : (
        flows?.map((flow) => <SmsFlowCard key={flow.id} flow={flow} />)
      )}

      <SmsLogsCard />
    </div>
  );
}

function SmsCredentialsCard() {
  const toast = useToast();
  const { data: status, isLoading } = useSmsCredentials();
  const save = useSaveSmsCredentials();
  const test = useSendTestSms();

  const [baseUrl, setBaseUrl] = useState('');
  const [publicKey, setPublicKey] = useState('');
  const [originator, setOriginator] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [suffix, setSuffix] = useState('');
  const [testTo, setTestTo] = useState('');

  useEffect(() => {
    if (!status) return;
    setBaseUrl(status.baseUrl ?? '');
    setPublicKey(status.publicKey ?? '');
    setOriginator(status.originator ?? '');
    setSuffix(status.suffix);
  }, [status]);

  async function submit() {
    try {
      await save.mutateAsync({
        baseUrl: baseUrl.trim() || undefined,
        publicKey: publicKey.trim() || undefined,
        originator: originator.trim() || undefined,
        suffix: suffix.trim() || undefined,
        // ცარიელი ველი = არსებული private key რჩება
        privateKey: privateKey.trim() || undefined,
      });
      setPrivateKey('');
      toast.success('შენახულია');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'შენახვა ვერ მოხერხდა');
    }
  }

  const [testResult, setTestResult] = useState<string | null>(null);

  async function sendTest() {
    setTestResult(null);
    try {
      const r = await test.mutateAsync({ to: testTo.trim() });
      toast.success(`გაიგზავნა: ${r.to}`);
      setTestResult(r.raw);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'გაგზავნა ვერ მოხერხდა';
      toast.error(message);
      setTestResult(message);
    }
  }

  if (isLoading) return null;

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <KeyRound className="size-4" /> SMS პროვაიდერი
        </span>
      }
    >
      <div className="mb-4">
        <Alert kind="info">
          Public Key მიდის მისამართის query-ში (<code>?publicKey=…</code>), Private Key კი
          <code>Authorization: Bearer</code> ჰედერით — BulkSMS.ge-ის დადასტურებული ფორმატი.
        </Alert>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="API მისამართი"
          value={baseUrl}
          onChange={(e) => setBaseUrl(e.target.value)}
          placeholder="https://restapi.example.ge/api/v1/sms/send"
          className="sm:col-span-2"
        />
        <Field
          label="Public Key"
          value={publicKey}
          onChange={(e) => setPublicKey(e.target.value)}
          placeholder="7dee71d8…"
        />
        <Field
          label="გამგზავნის სახელი (Originator)"
          value={originator}
          onChange={(e) => setOriginator(e.target.value)}
          placeholder="IT STEP"
          hint="მაქსიმუმ 11 სიმბოლო — ასე გამოჩნდება ადრესატთან"
        />
        <Field
          label="Private Key"
          type="password"
          autoComplete="new-password"
          value={privateKey}
          onChange={(e) => setPrivateKey(e.target.value)}
          placeholder={status?.privateKeyConfigured ? '•••••••• (ჩაწერილია)' : 'eyJhbGciOi…'}
          hint={
            status?.privateKeyConfigured
              ? 'ცარიელი ველი ტოვებს არსებულს — დაშიფრულად ინახება'
              : 'დაშიფრულად შეინახება და აღარ გამოჩნდება'
          }
        />

        <Field
          label="სავალდებულო ბოლოსართი"
          value={suffix}
          onChange={(e) => setSuffix(e.target.value)}
          hint="ავტომატურად ემატება ყოველ SMS-ს — ცარიელი ველი ნაგულისხმევს აბრუნებს"
        />

        <div className="flex items-end sm:col-span-2">
          <Button onClick={submit} disabled={save.isPending}>
            <Save className="size-4" /> შენახვა
          </Button>
        </div>
      </div>

      <div className="mt-6 border-t border-line pt-4">
        <p className="mb-2 text-sm text-ink-mute">
          საცდელი შეტყობინება. ბოლოსართი ავტომატურად დაერთვის.
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <Field
            label="ნომერი"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="599123456"
            className="min-w-56"
          />
          <Button variant="secondary" onClick={sendTest} disabled={!testTo || test.isPending}>
            <Send className="size-4" /> გაგზავნა
          </Button>
        </div>

        {testResult && (
          <pre className="mt-3 overflow-x-auto rounded bg-surface-2 p-3 text-[11px] whitespace-pre-wrap">
            {testResult}
          </pre>
        )}
      </div>
    </Card>
  );
}

function SmsFlowCard({ flow }: { flow: SmsFlow }) {
  const toast = useToast();
  const update = useUpdateSmsFlow();
  const runAbsence = useRunAbsenceSms();

  const [template, setTemplate] = useState(flow.template);
  const [schedule, setSchedule] = useState(flow.schedule ?? '');
  const [minutes, setMinutes] = useState(String((flow.conditions.minutesAfterStart as number) ?? 15));
  const [run, setRun] = useState<AbsenceSmsRun | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isAbsence = flow.event === 'student_absence';
  const dirty =
    template !== flow.template ||
    (schedule || null) !== flow.schedule ||
    (isAbsence && Number(minutes) !== ((flow.conditions.minutesAfterStart as number) ?? 15));

  async function save(patch: Partial<SmsFlow>) {
    setErrors({});
    try {
      await update.mutateAsync({ id: flow.id, ...patch });
      toast.success('შენახულია');
    } catch (err) {
      if (err instanceof ApiError) {
        setErrors(err.details ?? {});
        if (!err.details) toast.error(err.message);
      } else toast.error('შენახვა ვერ მოხერხდა');
    }
  }

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <MessageSquare className="size-4" /> {EVENT_LABEL[flow.event] ?? flow.event}
        </span>
      }
      action={
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={flow.isActive}
            onChange={(e) => void save({ isActive: e.target.checked })}
            className="size-4 accent-brand"
          />
          <span className={flow.isActive ? 'font-medium text-emerald-700' : 'text-ink-mute'}>
            {flow.isActive ? 'ჩართულია' : 'გამორთულია'}
          </span>
        </label>
      }
    >
      <div className="grid gap-4">
        {flow.description && <p className="text-[13px] text-ink-mute">{flow.description}</p>}

        {isAbsence && flow.isActive && (
          <Alert kind="error">
            ჩართულია: ლექციის დაწყებიდან <b>{minutes} წუთში</b> გაცდენილად მონიშნულ სტუდენტებს
            ავტომატურად მიუვათ SMS. თუ ლექტორი დამსწრეებს ამ დროისთვის ვერ მონიშნავს,
            შეტყობინება მათაც მიუვათ.
          </Alert>
        )}

        <div>
          <label className="mb-1 block text-sm font-medium">ტექსტი</label>
          <textarea
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            rows={3}
            className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-[13px]"
          />
          {errors.template && <p className="mt-1 text-xs text-rose-600">{errors.template}</p>}
          <p className="mt-2 text-xs text-ink-mute">
            ცვლადები:{' '}
            {flow.availableVariables.map((v) => (
              <code key={v} className="mr-1 rounded bg-surface-2 px-1">{`{{${v}}}`}</code>
            ))}
          </p>
          <p className="mt-1 rounded bg-surface-2 px-2 py-1 text-xs text-ink-mute">
            <b>ასე გაიგზავნება:</b> {flow.preview}
          </p>
        </div>

        {isAbsence && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="წუთი ლექციის დაწყებიდან"
              type="number"
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
            />
            <Field
              label="შემოწმების სიხშირე (cron)"
              value={schedule}
              error={errors.schedule}
              onChange={(e) => setSchedule(e.target.value)}
              hint='მაგ. "*/5 * * * *" — ყოველ 5 წუთში'
            />
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={() =>
              void save({
                template,
                schedule: isAbsence ? schedule || null : null,
                ...(isAbsence
                  ? { conditions: { ...flow.conditions, minutesAfterStart: Number(minutes) } }
                  : {}),
              })
            }
            disabled={!dirty || update.isPending}
          >
            <Save className="size-4" /> შენახვა
          </Button>

          {isAbsence && (
            <Button
              variant="secondary"
              onClick={() =>
                void runAbsence
                  .mutateAsync(true)
                  .then(setRun)
                  .catch(() => toast.error('ვერ შესრულდა'))
              }
              disabled={runAbsence.isPending}
            >
              <Play className="size-4" /> ვის მიუვიდოდა ახლა
            </Button>
          )}
        </div>

        {run && (
          <div className="rounded-lg border border-line bg-surface-2 p-3 text-sm">
            <p className="text-ink-mute">
              კანდიდატი: <b>{run.candidates}</b> (გაგზავნის გარეშე)
            </p>
            {run.reason && <p className="mt-1 text-amber-700">{run.reason}</p>}
            {run.preview && run.preview.length > 0 && (
              <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto text-xs">
                {run.preview.map((p, i) => (
                  <li key={i}>
                    {p.name} — {p.phone ?? 'ნომერი არ აქვს'} · {p.group}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

function SmsLogsCard() {
  const { data, isLoading } = useSmsLogs();

  return (
    <Card title="SMS-ის ისტორია">
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner className="size-5" />
        </div>
      ) : !data?.logs.length ? (
        <p className="py-6 text-center text-sm text-ink-mute">ჯერ არაფერი გაგზავნილა</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-ink-mute">
              <tr>
                <th className="pb-2">თარიღი</th>
                <th className="pb-2">მოვლენა</th>
                <th className="pb-2">ნომერი</th>
                <th className="pb-2">სტატუსი</th>
              </tr>
            </thead>
            <tbody>
              {data.logs.map((log) => (
                <tr key={log.id} className="border-t border-line">
                  <td className="py-2 whitespace-nowrap text-xs">
                    {new Date(log.createdAt).toLocaleString('ka-GE')}
                  </td>
                  <td className="py-2 text-xs">{EVENT_LABEL[log.event] ?? log.event}</td>
                  <td className="py-2 text-xs">{log.recipient}</td>
                  <td className="py-2">
                    <span
                      className={`rounded px-2 py-0.5 text-xs ${STATUS_STYLE[log.status] ?? ''}`}
                      title={log.error ?? undefined}
                    >
                      {log.status === 'SENT' ? 'გაიგზავნა' : log.status === 'FAILED' ? 'ჩავარდა' : 'გამოტოვდა'}
                    </span>
                    {log.error && (
                      <span className="ml-2 text-xs text-ink-mute">{log.error.slice(0, 60)}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
